"use client";

import { cn } from "@universe/ui/lib/utils";
import { type ReactNode, useEffect, useState } from "react";

import TransactionListSkeleton from "@/components/transaction-list-skeleton";

interface LedgerListTransitionProps {
  isInitialLoad: boolean;
  isEmpty: boolean;
  emptyMessage?: ReactNode;
  skeletonCount?: number;
  minHeight?: string;
  /** Optional bottom skeleton while paginating (page 2+) */
  showFetchSkeleton?: boolean;
  /** Override the default transaction-row skeleton (e.g. recipient cards) */
  skeleton?: ReactNode;
  children: ReactNode;
}

const SKELETON_FADE_MS = 200;

/**
 * Crossfades skeleton → content without AnimatePresence (translation-safe).
 * Item stagger is applied by LedgerEntryGroup via `revealingItemDelays`.
 *
 * When RTK already has a cache entry, consumers keep showing content and this
 * wrapper stays on the content branch — no load/reload flicker on background
 * refetch. Cold loads still get the skeleton → content crossfade.
 */
export default function LedgerListTransition({
  isInitialLoad,
  isEmpty,
  emptyMessage,
  skeletonCount = 4,
  minHeight = "250px",
  showFetchSkeleton = false,
  skeleton,
  children,
}: LedgerListTransitionProps) {
  const showContent = !isInitialLoad && !isEmpty;
  const showPrimarySkeleton = isInitialLoad || isEmpty;

  // Keep the skeleton mounted for one fade after content is ready so it can
  // crossfade out (translation-safe: no AnimatePresence). `skeletonFaded` is
  // flipped true only by the fade timer, and reset in the effect cleanup — never
  // synchronously in the effect body — so `retainSkeleton` stays a pure
  // render-time derivation. Seeded from `showContent` so a warm cache (content
  // shown on first paint) never flashes the skeleton.
  const [skeletonFaded, setSkeletonFaded] = useState(showContent);

  useEffect(() => {
    if (!showContent) return;

    const timeoutId = window.setTimeout(
      () => setSkeletonFaded(true),
      SKELETON_FADE_MS
    );
    return () => {
      window.clearTimeout(timeoutId);
      setSkeletonFaded(false);
    };
  }, [showContent]);

  const retainSkeleton = showPrimarySkeleton || (showContent && !skeletonFaded);

  const primarySkeleton = skeleton ?? (
    <TransactionListSkeleton
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
      skeletonCount={skeletonCount}
      minHeight={minHeight}
    />
  );

  return (
    <div className="relative w-full">
      {retainSkeleton && (
        <div
          className={cn(
            "transition-opacity ease-out",
            showContent
              ? "pointer-events-none absolute inset-x-0 top-0 z-0 opacity-0 duration-200"
              : "opacity-100 duration-0"
          )}
          aria-hidden={showContent}
        >
          {primarySkeleton}
        </div>
      )}

      {showContent && (
        <div className="relative z-[1] animate-list-content-enter">
          {children}
        </div>
      )}

      {showFetchSkeleton && (
        <div className="animate-list-fetch-enter">
          {skeleton ?? (
            <TransactionListSkeleton
              skeletonCount={skeletonCount}
              minHeight={minHeight}
            />
          )}
        </div>
      )}
    </div>
  );
}
